import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { BASE_URL } from "../../services/configUrls";

const SIDEBAR_WIDTH = 280;

const ProcessPage = () => {
  const { domainId, bookSlotId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [steps, setSteps] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [htmlContent, setHtmlContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [stepsLoading, setStepsLoading] = useState(true);

  // =========================
  // FIRST API
  // =========================
  useEffect(() => {
    getProcessSteps();
  }, []);

  // const getProcessSteps = async () => {
  //   try {
  //     setStepsLoading(true);

  //     const response = await axios.get(
  //       `${BASE_URL}/event/get-process-document`,
  //       {
  //         params: { domain_id: domainId },
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  //         },
  //       }
  //     );

  //     let formatted = [];
  //     response.data.forEach((item) => {
  //       const stepData = Object.values(item)[0];
  //       formatted.push(stepData);
  //     });

  //     setSteps(formatted);

  //     if (formatted.length > 0) {
  //       loadHtml(formatted[0]);
  //     }
  //   } catch (err) {
  //     console.log(err);
  //   } finally {
  //     setStepsLoading(false);
  //   }
  // };

  const getProcessSteps = async () => {
    try {
      setStepsLoading(true);

      const response = await axios.get(
        `${BASE_URL}/event/get-process-document`,
        {
          params: { domain_id: domainId, bookslot_id: bookSlotId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const apiData = response.data || [];

      const formatted = apiData
        .flatMap(item => Object.values(item))
        .filter(Boolean);

      setSteps(formatted);

      if (formatted?.length) {
        loadHtml(formatted[0]);
      }

    } catch (err) {
      console.error(
        err?.response?.data?.message || err.message
      );
    } finally {
      setStepsLoading(false);
    }
  };

  // =========================
  // SECOND API
  // =========================
  const loadHtml = async (step) => {
    try {
      setLoading(true);

      const fileName = step.process_document_filename;
      const secondApi = `https://erpapi.eduskillsfoundation.org/ongoingstudent/${fileName}`;

      let res = await axios.get(secondApi, { responseType: "text" });
      let cachedContent = res.data;

      // replace hash with link
      if (step.course_access_link) {
        cachedContent = cachedContent.replace(
          /https:\/\/8742d47f47673b6b4379f6a5ba885973ffb96bfd276e189c3cd191fa4287a882/g,
          step.course_access_link
        );
        cachedContent = cachedContent.replace(
          /8742d47f47673b6b4379f6a5ba885973ffb96bfd276e189c3cd191fa4287a882/g,
          step.course_access_link
        );
      }

      setHtmlContent(cachedContent);
    } catch (err) {
      console.error("Error loading process document template:", err);
      setHtmlContent("<h3>No document found</h3>");
    } finally {
      setLoading(false);
    }
  };

  // step click
  const handleStepClick = (index) => {
    setActiveStep(index);
    loadHtml(steps[index]);
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      const next = activeStep + 1;
      setActiveStep(next);
      loadHtml(steps[next]);
    }
  };

  const handlePrev = () => {
    if (activeStep > 0) {
      const prev = activeStep - 1;
      setActiveStep(prev);
      loadHtml(steps[prev]);
    }
  };

  // Full page loading
  if (stepsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#F3F4F6",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const currentStep = steps[activeStep];
  const domainName = steps.length > 0 ? steps[0].domain_name : "";

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* ── Blue Step Sidebar ── */}
      <Box
        sx={{
          width: SIDEBAR_WIDTH,
          minWidth: SIDEBAR_WIDTH,
          background: "linear-gradient(180deg, #1E3A5F 0%, #1A2F4A 100%)",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Sidebar Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <IconButton
            onClick={() => {
              const tab = location.state?.fromTab || "ongoing";
              navigate(`/faculty-events?tab=${tab}`);
            }}
            size="small"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, lineHeight: 1.3 }}
            >
              {domainName || "Process Steps"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.65rem" }}
            >
              Course Modules
            </Typography>
          </Box>
        </Box>

        {/* Step List - scrollable */}
        <Box sx={{ flex: 1, overflow: "auto", py: 1 }}>
          <List disablePadding>
            {steps.map((item, index) => {
              const isActive = activeStep === index;
              const isCompleted = index < activeStep;

              return (
                <ListItemButton
                  key={index}
                  selected={isActive}
                  onClick={() => handleStepClick(index)}
                  sx={{
                    py: 1,
                    px: 2,
                    borderLeft: isActive
                      ? "4px solid #60A5FA"
                      : "4px solid transparent",
                    backgroundColor: isActive
                      ? "rgba(96,165,250,0.15)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: isActive
                        ? "rgba(96,165,250,0.2)"
                        : "rgba(255,255,255,0.05)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {isCompleted ? (
                      <CheckCircleIcon
                        sx={{ color: "#34D399", fontSize: "1rem" }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={{
                          color: isActive
                            ? "#60A5FA"
                            : "rgba(255,255,255,0.4)",
                          fontSize: "1rem",
                        }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.steps_subject}
                    primaryTypographyProps={{
                      fontSize: "0.72rem",
                      fontWeight: isActive ? 700 : 400,
                      color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                      lineHeight: 1.3,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Box>

      {/* ── Main Content ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Blue Header with step subject */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            backgroundColor: "#1976D2",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {domainName}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.5 }}>
              &gt;
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600 }}
              noWrap
            >
              {currentStep?.steps_subject || `Step ${activeStep + 1}`}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ opacity: 0.7, whiteSpace: "nowrap" }}
          >
            Step {activeStep + 1} of {steps.length}
          </Typography>
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            backgroundColor: "#F3F4F6",
            p: 3,
          }}
        >
          <Paper
            sx={{
              p: 4,
              borderRadius: "12px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              minHeight: "calc(100vh - 220px)",
            }}
          >
            {/* HTML Content */}
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 8,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Box
                sx={{
                  "& img": { maxWidth: "100%", height: "auto" },
                  "& a": { color: "#2563EB", textDecoration: "underline" },
                  color: "#374151",
                  fontSize: "0.95rem",
                  lineHeight: 1.8,
                }}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            )}
          </Paper>

          {/* Previous / Next Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 2,
              pb: 2,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              disabled={activeStep === 0}
              onClick={handlePrev}
              sx={{
                textTransform: "none",
                borderColor: "#D1D5DB",
                color: "#374151",
                "&:hover": {
                  borderColor: "#9CA3AF",
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              endIcon={<NavigateNextIcon />}
              disabled={activeStep === steps.length - 1}
              onClick={handleNext}
              sx={{
                textTransform: "none",
                backgroundColor: "#1976D2",
                "&:hover": { backgroundColor: "#1565C0" },
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProcessPage;